import { Fragment, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useTranslation } from '../../hooks/i18n';
import { Nullable } from '../../types';
import { COLORS } from '../../themev2';
import ErrorMessage from '../../components/ErrorMessage';

interface TwoFactorAuthInputProps {
  onSubmit: (code: string) => void;
  verificationCode: string[];
  setVerificationCode: (code: string[]) => void;
  error: Nullable<string>;
  onError: (error: Nullable<string>) => void;
}

export default function TwoFactorAuthInput({
  onSubmit,
  verificationCode,
  setVerificationCode,
  error,
  onError,
}: TwoFactorAuthInputProps) {
  const { t } = useTranslation();

  // Create refs for each input field
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    // Focus first input on mount
    if (inputRefs[0].current) {
      const timer = setTimeout(() => {
        inputRefs[0].current?.focus();
      }, 150);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (index: number, value: string) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;

    const newVerificationCode = [...verificationCode];
    newVerificationCode[index] = value;
    setVerificationCode(newVerificationCode);
    onError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = async (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      // Focus previous input on backspace if current input is empty
      inputRefs[index - 1].current?.focus();
    }
    // Handle Enter key to submit the form if on the last input
    if (e.key === 'Enter' && verificationCode.length === 6) {
      await handleSubmit();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    // Only process if it looks like a verification code
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setVerificationCode(digits);

      // Focus the last input
      inputRefs[5].current?.focus();
    }
  };

  const handleSubmit = () => {
    const code = verificationCode.join('');
    if (code.length !== 6) {
      onError(t('common:twoFactor.invalidCodeError'));
      return;
    }
    onSubmit(code);
  };
  return (
    <Fragment>
      <Grid item xs={12} sx={{ marginTop: '2rem' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '2rem',
            width: '100%',
          }}
        >
          {verificationCode.map((digit, index) => (
            <Box
              key={index}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '64px',
                height: '80px',
              }}
            >
              <input
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                style={{
                  width: '100%',
                  height: '100%',
                  textAlign: 'center',
                  fontSize: '24px',
                  lineHeight: '32px',
                  fontWeight: 400,
                  border: `1px solid ${COLORS.gray[1000]}`,
                  borderRadius: '12px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.border = `1px solid ${COLORS.indigo[500]}`;
                }}
                onBlur={(e) => {
                  e.target.style.border = `1px solid ${COLORS.gray[1000]}`;
                }}
              />
            </Box>
          ))}
        </Box>
      </Grid>
      {error && (
        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: { xs: '100%', md: '50%', lg: '40%' },
            }}
          >
            <ErrorMessage
              errorMessage={error}
              onDismiss={() => {
                onError(null);
              }}
            />
          </Box>
        </Grid>
      )}
    </Fragment>
  );
}
